import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';

import { CacheLookupPolicy } from '@azure/msal-browser';
import { loginRequest } from '@configs/authConfig';
import { AuthSessionExpiredError } from '@shared/errors/auth-session-expired-error';
import { getMsalInstance } from '@shared/helpers/msal-instance/msalInstance';

import type { AcquireAccessTokenOptions } from './acquireAccessToken.types';

import {
  getAuthErrorCode,
  isExpiringSoon,
  isStrictlyExpired,
  isTerminalAuthError,
} from './acquireAccessToken.utils';

const inFlightRefreshes = new Map<string, Promise<string>>();

async function refreshIdToken(
  msal: PublicClientApplication,
  account: AccountInfo
): Promise<string> {
  const { idToken } = await msal.acquireTokenSilent({
    ...loginRequest,
    account,
    // Refresh-token only: no hidden-iframe fallback, so an expired session fails fast and deterministically.
    cacheLookupPolicy: CacheLookupPolicy.RefreshToken,
  });
  return idToken;
}

/**
 * Returns the ID token used as Bearer for API calls, refreshing it silently when needed.
 * Throws AuthSessionExpiredError when the user must re-authenticate.
 */
export async function acquireAccessToken(
  options: AcquireAccessTokenOptions = {}
): Promise<string> {
  const msal = await getMsalInstance();
  const account = msal.getActiveAccount();

  if (!account) {
    throw new AuthSessionExpiredError(
      'No active account - user must login first'
    );
  }

  const cachedToken = account.idToken;
  const exp = account.idTokenClaims?.exp;

  if (!options.forceRefresh && cachedToken && !isExpiringSoon(exp)) {
    return cachedToken;
  }

  const accountId = account.homeAccountId;
  let refresh = inFlightRefreshes.get(accountId);
  if (!refresh) {
    refresh = refreshIdToken(msal, account).finally(() => {
      inFlightRefreshes.delete(accountId);
    });
    inFlightRefreshes.set(accountId, refresh);
  }

  try {
    return await refresh;
  } catch (error) {
    // A forced refresh means the API already rejected the cached token, so it is not a valid fallback.
    if (!options.forceRefresh && cachedToken && !isStrictlyExpired(exp)) {
      return cachedToken;
    }

    if (isTerminalAuthError(error)) {
      throw new AuthSessionExpiredError(
        `Session expired (${getAuthErrorCode(error)})`
      );
    }

    throw error;
  }
}
